import { useEffect, useState } from "react";
import { listMembers } from "../services/memberService";
import { useAuth } from "../auth/AuthContext";

/**
 * useTenantRole — descobre o papel (OWNER | ADMIN | MEMBER) do usuário
 * autenticado dentro do tenant atual.
 *
 * O perfil de `/auth/me` (AuthContext) NÃO carrega o papel do membro — ele é
 * uma propriedade de `TenantMember`. Espelhamos o padrão já usado em
 * `Professionals.js`: buscar a lista de membros (`GET /members`) e casar pelo
 * `userId` do usuário logado para derivar o papel.
 *
 * A resposta é cacheada em módulo (uma requisição por sessão), evitando refetch
 * a cada navegação — o `AppLayout` remonta em toda rota autenticada e consome
 * este hook para gatear o item de menu "Setores".
 */
let membersPromise = null;

/** Invalida o cache (ex.: após troca de tenant / logout). */
export function clearTenantRoleCache() {
  membersPromise = null;
}

export function useTenantRole() {
  const { user } = useAuth();
  const userId = user?.id;
  // `memberId` é o `TenantMember.id` do usuário logado (≠ `user.id`). Ações que
  // operam sobre o membro — como "Pegar conversa" (PATCH .../assign com
  // `assignedToId`) — precisam desse id, não do id do usuário.
  const [state, setState] = useState({
    role: null,
    memberId: null,
    loading: true,
  });

  useEffect(() => {
    let active = true;

    if (!userId) {
      setState({ role: null, memberId: null, loading: false });
      return undefined;
    }

    if (!membersPromise) {
      membersPromise = listMembers()
        .then((res) => res.data)
        .catch(() => {
          // Falha de rede não deve travar o app: sem papel conhecido, os
          // recursos gateados por role simplesmente não aparecem.
          membersPromise = null;
          return [];
        });
    }

    setState((prev) => ({ ...prev, loading: true }));

    membersPromise.then((members) => {
      if (!active) return;
      const me = Array.isArray(members)
        ? members.find((m) => m.userId === userId)
        : null;
      setState({
        role: me?.role ?? null,
        memberId: me?.id ?? null,
        loading: false,
      });
    });

    return () => {
      active = false;
    };
  }, [userId]);

  return state;
}
